import React from 'react';
import FormNumberInput from '@/components/UI/FormNumberInput';
import { DEFAULT_SUBSCRIPTION_PRIORITY } from './governanceHelpers';

export interface GovernancePriorityStrategy {
    strategyId: number;
    name: string;
    isActive: boolean;
}

interface GovernanceSubscriptionPriorityTableProps {
    strategies: GovernancePriorityStrategy[];
    /** strategyId → priority (lower ranks first). */
    priorities: Record<number, number>;
    onChange: (strategyId: number, priority: number) => void;
}

/** A priority always resolves to a number, so a cleared field falls back to the default rank. */
const resolvePriority = (value: number | null): number => (value !== null && value >= 0 ? value : DEFAULT_SUBSCRIPTION_PRIORITY);

/**
 * Per-strategy ranking priority editor, used when the policy ranks competing entries by subscription
 * priority (and as a tie-break otherwise). Lower number = ranks first. Only strategies with a linked
 * trading profile are shown, since a priority row needs the profile id.
 */
const GovernanceSubscriptionPriorityTable: React.FC<GovernanceSubscriptionPriorityTableProps> = ({ strategies, priorities, onChange }) => (
    <div className="border-t border-zinc-800 pt-4">
        <div className="mb-2">
            <h6 className="text-base font-semibold">Strategy ranking priority</h6>
            <p className="text-sm text-zinc-500">
                When candidates compete for the same entity, lower priority ranks first. Used directly by the “Strategy subscription
                priority” metric, and as a tie-break for the others.
            </p>
        </div>

        <div className="overflow-x-auto">
            <table className="min-w-full border-collapse bg-zinc-800 text-white">
                <thead>
                    <tr className="bg-zinc-700 text-left">
                        <th className="px-3 py-2">Strategy</th>
                        <th className="w-40 px-3 py-2">Priority</th>
                    </tr>
                </thead>
                <tbody>
                    {strategies.map((strategy, index) => (
                        <tr className={index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'} key={strategy.strategyId}>
                            <td className="border-t border-zinc-700 px-3 py-2">
                                {strategy.name}
                                {!strategy.isActive && <span className="ml-2 text-xs text-zinc-500">(inactive)</span>}
                            </td>
                            <td className="border-t border-zinc-700 px-3 py-2">
                                <FormNumberInput
                                    integer
                                    noMargin
                                    id={`governancePriority-${strategy.strategyId}`}
                                    min={0}
                                    value={priorities[strategy.strategyId] ?? DEFAULT_SUBSCRIPTION_PRIORITY}
                                    onValueChange={(value) => onChange(strategy.strategyId, resolvePriority(value))}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

export default GovernanceSubscriptionPriorityTable;
